import React from 'react';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card } from '@/components/common';
import { formatCurrency } from '@/lib/format';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';
import { Wallet, ArrowDownLeft, ArrowUpRight, Gauge } from 'lucide-react';

const CashIntelligence = () => {
  const { t, tx, lang, data } = useApp();
  const c = data.cash;

  const stats = [
    { key: 'available', value: c.available, icon: Wallet, color: '#06B6D4' },
    { key: 'inflows', value: c.inflows, icon: ArrowDownLeft, color: '#10B981' },
    { key: 'outflows', value: -c.outflows, icon: ArrowUpRight, color: '#EF4444' },
  ];

  return (
    <div>
      <PageHeader title={t('cash.title')} subtitle={t('cash.subtitle')} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
        {stats.map((s) => (
          <Card key={s.key} className="p-5" hover data-testid={`cash-${s.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] tracking-[0.15em] uppercase font-semibold text-zinc-400">{t(`cash.${s.key}`)}</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}1A` }}><s.icon className="w-4 h-4" style={{ color: s.color }} /></div>
            </div>
            <p className="font-heading text-2xl font-bold font-mono-num" style={{ color: s.color }}>{formatCurrency(s.value, lang)}</p>
          </Card>
        ))}
        <Card className="p-5" hover data-testid="cash-runway">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] tracking-[0.15em] uppercase font-semibold text-zinc-400">{t('cash.runway')}</span>
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center"><Gauge className="w-4 h-4 text-violet-600" /></div>
          </div>
          <p className="font-heading text-2xl font-bold font-mono-num text-zinc-900">{c.runway} <span className="text-base font-medium text-zinc-400">{t('cash.months')}</span></p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6" data-testid="cash-timeline">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('cash.timeline')}</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={c.timeline} margin={{ left: -5, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="cashg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" vertical={false} />
                <XAxis dataKey="week" tickFormatter={(w) => `S${w + 1}`} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v, lang)} contentStyle={{ borderRadius: 12, border: '1px solid #e4e4e7', fontSize: 12 }} labelFormatter={(w) => `Semana ${w + 1}`} />
                <ReferenceLine y={200000} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: lang === 'es' ? 'Colchón' : 'Buffer', fontSize: 10, fill: '#F59E0B', position: 'insideTopLeft' }} />
                <Area type="monotone" dataKey="balance" stroke="#06B6D4" strokeWidth={2.5} fill="url(#cashg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6" data-testid="cash-movements">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('cash.movements')}</p>
          <div className="space-y-3">
            {c.movements.map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.type === 'in' ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                  {m.type === 'in' ? <ArrowDownLeft className="w-4 h-4 text-emerald-600" /> : <ArrowUpRight className="w-4 h-4 text-rose-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">{tx(m.name)}</p>
                  <p className="text-xs text-zinc-400">{m.date}</p>
                </div>
                <span className={`font-mono-num text-sm font-semibold ${m.type === 'in' ? 'text-emerald-600' : 'text-rose-500'}`}>{formatCurrency(m.amount, lang)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CashIntelligence;
